import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Factory, Clock, Shield, Award, TrendingUp, CheckCircle2 } from 'lucide-react';

/* ── SVG Icons for each manufacturing step ── */
const StepIcons = {
  Design: () => (
    <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
      <rect x="10" y="8" width="34" height="44" rx="2" stroke="currentColor" strokeWidth="2.5" fill="none"/>
      <line x1="16" y1="18" x2="38" y2="18" stroke="currentColor" strokeWidth="2"/>
      <line x1="16" y1="26" x2="38" y2="26" stroke="currentColor" strokeWidth="2"/>
      <line x1="16" y1="34" x2="30" y2="34" stroke="currentColor" strokeWidth="2"/>
      <path d="M40 32L54 18V56H40V32Z" stroke="currentColor" strokeWidth="2.5" fill="currentColor" fillOpacity="0.1"/>
      <path d="M44 52L50 46" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M44 46L50 40" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  Steel: () => (
    <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
      <rect x="8" y="22" width="48" height="6" rx="3" stroke="currentColor" strokeWidth="2.5"/>
      <rect x="8" y="36" width="48" height="6" rx="3" stroke="currentColor" strokeWidth="2.5"/>
      <line x1="18" y1="28" x2="18" y2="36" stroke="currentColor" strokeWidth="2"/>
      <line x1="32" y1="28" x2="32" y2="36" stroke="currentColor" strokeWidth="2"/>
      <line x1="46" y1="28" x2="46" y2="36" stroke="currentColor" strokeWidth="2"/>
      <path d="M4 16L14 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M4 16L14 22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="54" cy="50" r="6" stroke="currentColor" strokeWidth="2"/>
      <path d="M58 54L62 58" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  ),
  Mould: () => (
    <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
      <path d="M8 48V16H56V48" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M4 48H60" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <rect x="14" y="22" width="36" height="20" rx="1" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3"/>
      <circle cx="11" cy="16" r="2" fill="currentColor"/>
      <circle cx="53" cy="16" r="2" fill="currentColor"/>
      <path d="M22 52L22 48" stroke="currentColor" strokeWidth="2"/>
      <path d="M42 52L42 48" stroke="currentColor" strokeWidth="2"/>
    </svg>
  ),
  Rebar: () => (
    <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
      <rect x="12" y="12" width="40" height="40" rx="2" stroke="currentColor" strokeWidth="2.5"/>
      <line x1="20" y1="12" x2="20" y2="52" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3"/>
      <line x1="32" y1="12" x2="32" y2="52" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3"/>
      <line x1="44" y1="12" x2="44" y2="52" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3"/>
      <line x1="12" y1="22" x2="52" y2="22" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3"/>
      <line x1="12" y1="32" x2="52" y2="32" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3"/>
      <line x1="12" y1="42" x2="52" y2="42" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3"/>
      <path d="M6 8L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M58 56L52 52" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  Batch: () => (
    <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
      <path d="M16 8H48L52 24H12L16 8Z" stroke="currentColor" strokeWidth="2.5"/>
      <rect x="12" y="24" width="40" height="28" rx="2" stroke="currentColor" strokeWidth="2.5"/>
      <line x1="22" y1="24" x2="22" y2="52" stroke="currentColor" strokeWidth="1.5"/>
      <line x1="42" y1="24" x2="42" y2="52" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="32" cy="38" r="6" stroke="currentColor" strokeWidth="2"/>
      <path d="M32 34V38L35 40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M26 56L26 52" stroke="currentColor" strokeWidth="2"/>
      <path d="M38 56L38 52" stroke="currentColor" strokeWidth="2"/>
    </svg>
  ),
  Pour: () => (
    <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
      <path d="M24 6V20" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
      <path d="M20 6H28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M22 20C22 20 18 28 18 32" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M26 20C26 20 30 28 30 32" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <rect x="10" y="32" width="44" height="22" rx="2" stroke="currentColor" strokeWidth="2.5"/>
      <path d="M10 42H54" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 3"/>
      <path d="M40 20V28M38 24H42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M48 14V22M46 18H50" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  Cure: () => (
    <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
      <rect x="10" y="20" width="44" height="30" rx="3" stroke="currentColor" strokeWidth="2.5"/>
      <path d="M18 20V14H46V20" stroke="currentColor" strokeWidth="2.5"/>
      <path d="M20 32C22 28 26 28 28 32C30 36 34 36 36 32C38 28 42 28 44 32" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M20 40C22 36 26 36 28 40C30 44 34 44 36 40C38 36 42 36 44 40" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="32" cy="10" r="4" stroke="currentColor" strokeWidth="2"/>
      <path d="M32 6V2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M36 10H40" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M24 10H28" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  Test: () => (
    <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
      <circle cx="32" cy="32" r="22" stroke="currentColor" strokeWidth="2.5"/>
      <path d="M32 14V32L42 42" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="32" cy="32" r="3" fill="currentColor"/>
      <path d="M18 10L14 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M46 10L50 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M22 56L26 60" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M42 56L38 60" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M20 20L24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="0" opacity="0.5"/>
    </svg>
  ),
  Demould: () => (
    <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
      <rect x="14" y="30" width="36" height="22" rx="2" stroke="currentColor" strokeWidth="2.5"/>
      <rect x="20" y="22" width="24" height="14" rx="1" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.1"/>
      <path d="M32 8V18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M26 14L32 8L38 14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="14" y1="56" x2="50" y2="56" stroke="currentColor" strokeWidth="2.5"/>
      <line x1="18" y1="56" x2="18" y2="52" stroke="currentColor" strokeWidth="2"/>
      <line x1="46" y1="56" x2="46" y2="52" stroke="currentColor" strokeWidth="2"/>
    </svg>
  ),
  Transport: () => (
    <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
      <rect x="4" y="24" width="40" height="20" rx="2" stroke="currentColor" strokeWidth="2.5"/>
      <path d="M44 30H56L60 38V44H44V30Z" stroke="currentColor" strokeWidth="2.5"/>
      <circle cx="16" cy="48" r="4" stroke="currentColor" strokeWidth="2.5"/>
      <circle cx="52" cy="48" r="4" stroke="currentColor" strokeWidth="2.5"/>
      <line x1="20" y1="48" x2="48" y2="48" stroke="currentColor" strokeWidth="2"/>
      <rect x="10" y="28" width="28" height="4" rx="1" fill="currentColor" fillOpacity="0.15"/>
      <rect x="10" y="34" width="28" height="4" rx="1" fill="currentColor" fillOpacity="0.15"/>
    </svg>
  ),
  Erect: () => (
    <svg viewBox="0 0 64 64" fill="none" className="w-full h-full">
      <line x1="16" y1="56" x2="16" y2="10" stroke="currentColor" strokeWidth="3"/>
      <line x1="16" y1="10" x2="50" y2="10" stroke="currentColor" strokeWidth="2.5"/>
      <line x1="50" y1="10" x2="50" y2="24" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="42" y="24" width="16" height="12" rx="1" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.1"/>
      <line x1="10" y1="56" x2="22" y2="56" stroke="currentColor" strokeWidth="2.5"/>
      <line x1="12" y1="56" x2="16" y2="42" stroke="currentColor" strokeWidth="2"/>
      <line x1="20" y1="56" x2="16" y2="42" stroke="currentColor" strokeWidth="2"/>
      <rect x="30" y="40" width="12" height="16" rx="1" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3"/>
      <path d="M36 36V40" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
};

const processSteps = [
  { id: 1, title: 'Design', icon: 'Design', phase: 'planning', color: '#2B5278' },
  { id: 2, title: 'Steel Fab', icon: 'Steel', phase: 'planning', color: '#2B5278' },
  { id: 3, title: 'Mould Prep', icon: 'Mould', phase: 'factory', color: '#1E3A5F' },
  { id: 4, title: 'Rebar', icon: 'Rebar', phase: 'factory', color: '#1E3A5F' },
  { id: 5, title: 'Batching', icon: 'Batch', phase: 'factory', color: '#1E3A5F' },
  { id: 6, title: 'Pour', icon: 'Pour', phase: 'factory', color: '#1D4ED8' },
  { id: 7, title: 'Steam Cure', icon: 'Cure', phase: 'quality', color: '#1E40AF' },
  { id: 8, title: 'QC Test', icon: 'Test', phase: 'quality', color: '#1E40AF' },
  { id: 9, title: 'Demould', icon: 'Demould', phase: 'quality', color: '#1E40AF' },
  { id: 10, title: 'Transport', icon: 'Transport', phase: 'delivery', color: '#1E3A8A' },
  { id: 11, title: 'Erect', icon: 'Erect', phase: 'delivery', color: '#1E3A8A' },
];

const phaseLabels = {
  planning: { label: 'Engineering', bg: 'bg-slate-100', border: 'border-slate-300', text: 'text-slate-700' },
  factory: { label: 'Factory Production', bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-800' },
  quality: { label: 'Quality & Finish', bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-600' },
  delivery: { label: 'Site Delivery', bg: 'bg-stone-100', border: 'border-stone-300', text: 'text-stone-700' },
};

const WhatIsPrecast = () => {
  return (
    <div className="min-h-screen pt-20 lg:pt-24">
      <Helmet>
        <title>What is Precast Concrete? | Arthaa Build — Precast Technology Explained</title>
        <meta name="description" content="Learn what precast concrete is, how it's made in 11 factory-controlled steps, and why Arthaa Build uses precast technology to build 50% faster with superior quality. Precast concrete construction India." />
        <link rel="canonical" href="https://arthaabuild.com/what-is-precast" />
      </Helmet>

      {/* Hero */}
      <section className="section-padding bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="font-serif font-bold text-5xl sm:text-6xl mb-6">
              What is Precast Concrete?
            </h1>
            <p className="text-2xl text-slate-300 leading-relaxed mb-8">
              The smarter way to build — engineered in a factory, assembled on site, built to last a century
            </p>
            <p className="text-xl text-slate-400">
              Reinforced concrete manufactured in a controlled factory environment, cured to full design strength, then transported and assembled on-site
            </p>
          </motion.div>
        </div>
      </section>

      {/* Definition */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-20">
          <h2 className="font-serif font-bold text-4xl text-slate-900 mb-8">The Core Concept</h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            <strong>Pre</strong> means before. <strong>Cast</strong> means poured into a mould. Precast concrete is concrete that is <strong>cast before it arrives at the building site</strong>, as opposed to conventional cast-in-situ construction where concrete is mixed, poured, and cured entirely on site.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            This factory-first approach results in a <strong>stronger, more dimensionally accurate, and more durable</strong> structural element — whether it's a column, wall panel, floor slab, beam, or staircase.
          </p>
          <div className="bg-[#2B5278]/5 border-l-4 border-[#2B5278] p-6 rounded-r-lg">
            <p className="text-slate-700 font-medium">
              The result: Elements arrive on-site already at full 28-day design strength, ready to be lifted by crane and connected into a finished structure in days, not months.
            </p>
          </div>
        </div>
      </section>

      {/* How It's Made - Visual Chain Cycle */}
      <section className="section-padding bg-slate-950 overflow-hidden" data-testid="precast-process-flow">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-6"
          >
            <p className="text-sm uppercase tracking-widest text-sky-300 font-semibold mb-3">The Manufacturing Chain</p>
            <h2 className="font-serif font-bold text-4xl sm:text-5xl text-white mb-4">How Precast is Made</h2>
            <p className="text-slate-400 max-w-xl mx-auto">11-step precision cycle from blueprint to building</p>
          </motion.div>

          {/* Phase Legend */}
          <div className="flex flex-wrap justify-center gap-4 mb-14">
            {Object.entries(phaseLabels).map(([key, val]) => (
              <div key={key} className={`px-4 py-1.5 rounded-full text-xs font-semibold border ${val.bg} ${val.border} ${val.text}`}>
                {val.label}
              </div>
            ))}
          </div>

          {/* Visual Chain — Desktop: flowing grid with connectors */}
          <div className="hidden lg:block relative">
            {/* Row 1: Steps 1-6 */}
            <div className="flex items-center justify-center gap-2 mb-4">
              {processSteps.slice(0, 6).map((step, i) => (
                <React.Fragment key={step.id}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="flex flex-col items-center group"
                  >
                    <div className="relative w-[130px] h-[130px] rounded-2xl border-2 border-slate-700/60 bg-slate-900 flex items-center justify-center transition-all duration-300 group-hover:border-sky-400 group-hover:shadow-[0_0_30px_rgba(43,82,120,0.25)]">
                      <div className="absolute -top-3 -left-3 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white" style={{ background: step.color }}>
                        {step.id}
                      </div>
                      <div className="w-16 h-16 text-slate-400 group-hover:text-sky-300 transition-colors duration-300">
                        {StepIcons[step.icon]()}
                      </div>
                    </div>
                    <span className="mt-3 text-xs font-semibold text-slate-400 group-hover:text-sky-200 tracking-wide uppercase transition-colors">{step.title}</span>
                  </motion.div>
                  {i < 5 && (
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 + 0.1 }}
                      className="flex-shrink-0"
                    >
                      <svg width="40" height="20" viewBox="0 0 40 20" className="text-slate-600">
                        <line x1="0" y1="10" x2="30" y2="10" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
                        <path d="M28 4L36 10L28 16" fill="none" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </motion.div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Curved connector from Row 1 to Row 2 */}
            <div className="flex justify-end pr-[72px] mb-4">
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <svg width="50" height="60" viewBox="0 0 50 60" className="text-slate-600">
                  <path d="M25 0V20C25 35 25 35 25 50" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
                  <path d="M19 44L25 54L31 44" fill="none" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </motion.div>
            </div>

            {/* Row 2: Steps 7-11 (reversed direction) */}
            <div className="flex items-center justify-center gap-2">
              {[...processSteps.slice(6)].reverse().map((step, i) => (
                <React.Fragment key={step.id}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + i * 0.08 }}
                    className="flex flex-col items-center group"
                  >
                    <div className="relative w-[130px] h-[130px] rounded-2xl border-2 border-slate-700/60 bg-slate-900 flex items-center justify-center transition-all duration-300 group-hover:border-sky-400 group-hover:shadow-[0_0_30px_rgba(43,82,120,0.25)]">
                      <div className="absolute -top-3 -left-3 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white" style={{ background: step.color }}>
                        {step.id}
                      </div>
                      <div className="w-16 h-16 text-slate-400 group-hover:text-sky-300 transition-colors duration-300">
                        {StepIcons[step.icon]()}
                      </div>
                    </div>
                    <span className="mt-3 text-xs font-semibold text-slate-400 group-hover:text-sky-200 tracking-wide uppercase transition-colors">{step.title}</span>
                  </motion.div>
                  {i < 4 && (
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + i * 0.08 + 0.1 }}
                      className="flex-shrink-0"
                    >
                      <svg width="40" height="20" viewBox="0 0 40 20" className="text-slate-600">
                        <line x1="10" y1="10" x2="40" y2="10" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
                        <path d="M12 4L4 10L12 16" fill="none" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </motion.div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Mobile: Vertical chain */}
          <div className="lg:hidden space-y-1">
            {processSteps.map((step, i) => (
              <React.Fragment key={step.id}>
                <motion.div
                  initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-center gap-4"
                >
                  <div className="relative w-20 h-20 rounded-xl border-2 border-slate-700/60 bg-slate-900 flex items-center justify-center flex-shrink-0">
                    <div className="absolute -top-2 -left-2 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ background: step.color }}>
                      {step.id}
                    </div>
                    <div className="w-10 h-10 text-sky-300">
                      {StepIcons[step.icon]()}
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-slate-300 tracking-wide">{step.title}</span>
                </motion.div>
                {i < processSteps.length - 1 && (
                  <div className="flex justify-start pl-9">
                    <svg width="4" height="20" viewBox="0 0 4 20" className="text-slate-600">
                      <line x1="2" y1="0" x2="2" y2="16" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3"/>
                      <path d="M0 14L2 20L4 14" fill="currentColor"/>
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Summary Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] rounded-2xl p-8 lg:p-10 flex flex-col lg:flex-row items-center justify-between gap-6"
          >
            <div className="flex items-center gap-6">
              <div className="hidden sm:flex items-center gap-3 text-white/90">
                <svg width="40" height="40" viewBox="0 0 40 40" className="text-white">
                  <rect x="4" y="10" width="14" height="26" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
                  <rect x="22" y="4" width="14" height="32" rx="1" stroke="currentColor" strokeWidth="2" fill="none"/>
                  <line x1="18" y1="23" x2="22" y2="23" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <svg width="24" height="24" viewBox="0 0 24 24" className="text-white/50">
                  <path d="M4 12H20M14 6L20 12L14 18" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
                <svg width="40" height="40" viewBox="0 0 40 40" className="text-white">
                  <rect x="4" y="16" width="32" height="16" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
                  <circle cx="14" cy="36" r="3" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="30" cy="36" r="3" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <svg width="24" height="24" viewBox="0 0 24 24" className="text-white/50">
                  <path d="M4 12H20M14 6L20 12L14 18" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
                <svg width="40" height="40" viewBox="0 0 40 40" className="text-white">
                  <line x1="10" y1="36" x2="10" y2="4" stroke="currentColor" strokeWidth="2.5"/>
                  <line x1="10" y1="4" x2="36" y2="4" stroke="currentColor" strokeWidth="2"/>
                  <rect x="28" y="10" width="10" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2"/>
                  <rect x="14" y="24" width="12" height="12" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              </div>
            </div>
            <div className="text-center lg:text-left">
              <h3 className="font-serif font-bold text-2xl lg:text-3xl text-white mb-2">Factory  →  Transport  →  Assemble</h3>
              <p className="text-white/80">Precision at every step. Quality you can measure.</p>
            </div>
            <Link to="/construction" className="flex-shrink-0 bg-white text-[#1E3A5F] font-semibold px-8 py-3 rounded-lg hover:bg-slate-50 transition-colors">
              Learn More
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Precast Outperforms */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="font-serif font-bold text-4xl text-slate-900 mb-4">
              Why Precast Outperforms Conventional Construction
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="luxury-card bg-white p-8 rounded-xl"
            >
              <Clock className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">30-50% Faster</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Factory production runs parallel to site foundation work. A 50,000 sq ft warehouse: 5-7 months vs. 12-15 months for cast-in-situ. Saves on construction loan interest.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="luxury-card bg-white p-8 rounded-xl"
            >
              <Shield className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">Superior Quality</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Factory-controlled mix design, water-cement ratio, compaction, and curing. Every element comes with a test certificate. Eliminates on-site variations and extreme climate risks.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="luxury-card bg-white p-8 rounded-xl"
            >
              <Award className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">IS 13920 Compliant</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Produced to IS 456 and IS 15916 standards with seismic detailing (IS 13920). Measurably superior structural performance in earthquake-prone zones.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="luxury-card bg-white p-8 rounded-xl"
            >
              <Factory className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">Less Labour Intensive</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Factory: 18-22 skilled workers. Site erection: 8-12 workers. Reduces dependence on large, variable labour markets that are becoming increasingly tight.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="luxury-card bg-white p-8 rounded-xl"
            >
              <TrendingUp className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">Sustainable</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                40-50% less waste, 8-12% less cement, 30-40% less water. Precise batching prevents material rejection. Lower lifecycle carbon footprint.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="luxury-card bg-white p-8 rounded-xl"
            >
              <CheckCircle2 className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">Precision Engineering</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                CNC bar bending, computerized batching (0.1% accuracy), millimetric tolerances (±2mm). Quality impossible to achieve in site conditions.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Range */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <h2 className="font-serif font-bold text-4xl text-slate-900 mb-12 text-center">What Precast Makes</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { title: 'Structural Columns', desc: '300x300mm to 600x600mm sections, 4m to 16m lengths. Carry vertical loads with starter sockets and connection plates.' },
              { title: 'Wall Cladding Panels', desc: '200mm thick, 3m wide, 6m tall. Various finishes: exposed aggregate, sandblasted, acid-etched. Form external building skin.' },
              { title: 'Pre-stressed TT Slabs', desc: '15-24m clear spans without internal columns. Double-T profile ideal for warehouses and factories.' },
              { title: 'Hollow Core Slabs', desc: '30-40% lighter than solid slabs. Multi-storey floor systems with structural performance maintained.' },
              { title: 'Precast Beams', desc: 'Span between columns supporting slabs. Pre-stressed beams achieve longer spans with shallower depths.' },
              { title: 'Boundary Walls', desc: '2.1m tall panels installing at 10 running metres/hour. 30-45% cheaper than brick, 1-2 days vs 3-4 weeks.' }
            ].map((product, i) => (
              <div key={i} className="luxury-card bg-slate-50 p-6 rounded-lg">
                <h3 className="font-serif font-bold text-lg text-slate-900 mb-2">{product.title}</h3>
                <p className="text-sm text-slate-600">{product.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* India Market */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-20">
          <h2 className="font-serif font-bold text-4xl text-slate-900 mb-8">Precast in India</h2>
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="font-serif font-bold text-2xl text-slate-900 mb-4">Why It Matters for Your Project</h3>
              <p className="text-slate-600 mb-4">
                India's construction landscape is shifting. Traditional methods can no longer deliver the speed, quality, and cost predictability that modern projects demand.
              </p>
              <p className="text-slate-600 mb-6">
                Government infrastructure mandates, tight project timelines, and the need for consistent quality are driving developers, contractors, and investors toward precast solutions.
              </p>
              <div className="bg-white p-6 rounded-lg border-l-4 border-[#2B5278]">
                <p className="font-semibold text-slate-900 mb-2">Government Backing</p>
                <p className="text-sm text-slate-600">Ministry of Road Transport mandates precast in highway projects. Target: 75-80% precast usage in bridges, tunnels, and roads nationwide.</p>
              </div>
            </div>
            <div>
              <h3 className="font-serif font-bold text-2xl text-slate-900 mb-4">Proven Success Stories</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#2B5278] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600"><strong>Metro Rail Systems:</strong> Delhi, Mumbai, Bengaluru use precast segmental girders — assembled without disrupting traffic</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#2B5278] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600"><strong>Mass Housing:</strong> 1,000+ apartment project in Ranchi delivered with 3D Modular Precast</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#2B5278] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600"><strong>High-Rise Towers:</strong> 180-meter, 43-storey buildings in Hyderabad using precast technology</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#2B5278] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600"><strong>Speed Record:</strong> 12-storey residential tower (96 apartments) completed in just 96 days</span>
                </li>
              </ul>
              <div className="mt-6 bg-[#2B5278]/5 p-4 rounded-lg">
                <p className="text-sm text-slate-700">
                  <strong>The Takeaway:</strong> If government projects and major developers trust precast for speed and quality, it's the proven choice for your project too.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* North India Focus */}
      <section className="section-padding bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-20">
          <h2 className="font-serif font-bold text-4xl mb-8 text-center">The North India Opportunity</h2>
          <p className="text-xl text-white/90 mb-8 text-center max-w-3xl mx-auto">
            North India is becoming the fastest-growing market for industrial precast construction
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <h3 className="font-semibold text-lg mb-3">Demand Drivers</h3>
              <ul className="space-y-2 text-white/90 text-sm">
                <li>• Delhi-Mumbai & Amritsar-Kolkata Industrial Corridors</li>
                <li>• E-commerce logistics revolution (Grade A warehousing)</li>
                <li>• PLI Scheme funding new manufacturing facilities</li>
                <li>• NH-44 Corridor warehousing boom</li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <h3 className="font-semibold text-lg mb-3">Perfect For</h3>
              <ul className="space-y-2 text-white/90 text-sm">
                <li>• Large clear-span industrial buildings</li>
                <li>• Tight project deadlines</li>
                <li>• Total cost of ownership focus</li>
                <li>• Repetitive grid structures</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-20 text-center">
          <h2 className="font-serif font-bold text-4xl text-slate-900 mb-6">Ready to Build with Arthaa Build Precast?</h2>
          <p className="text-xl text-slate-600 mb-8">
            Discover how Arthaa Build can accelerate your project with factory-engineered precision
          </p>
          <Link to="/contact" className="inline-block bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] text-white font-semibold px-10 py-4 rounded-md hover:shadow-lg transition-all">
            Discuss Your Project
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WhatIsPrecast;